import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DealCardRow, DealGridRow, DealRow } from './DealRows';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { UndoToast } from './UndoToast';
import { SheetShell } from './SheetShell';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/** PipelineGrid — #62 Phase 2 sales_pipeline (UXeng's per-screen spec §1): a TABLE, not a board (the
 *  comp columns are Deal/Stage/Last-activity/Value/Owner; task_board is the only kanban in this
 *  phase). Same responsive tier-switch as Leads/Contacts/Accounts. Stage edit is optimistic -> PUT
 *  /api/deals/:id/stage -> UndoToast, same pattern as the Leads grid's attribute edits. */
export const PipelineGrid: React.FC<{
  selectedId?: string | null;
  onOpenDeal: (deal: DealRow) => void;
  /** Bumped by the parent after create/close/delete succeeds (#62 Pipeline depth spec) — this
   *  component owns its own fetch, so it needs an explicit re-fetch signal. */
  refreshKey?: number;
}> = ({ selectedId, onOpenDeal, refreshKey }) => {
  const { t } = useTranslation('common');
  const [editing, setEditing] = useState<{ deal: DealRow; anchor: HTMLElement; shape: 'sheet' | 'popover' } | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'undo' | 'error'; onUndo?: () => void } | null>(null);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surface 5 of 10). The old code was the WORST variant of the family:
  // a non-OK response was EXPLICITLY mapped to { deals: [] }, so an HTTP error rendered an empty
  // pipeline by design — and an empty pipeline is a business emergency the operator must be able
  // to tell from a network blip.
  type PipelineTotals = { unweighted: number; weighted: { value: number; weightedStages: number; totalStages: number } | null };
  const list = useListFetch<{ deals: DealRow[]; staleAfterDays: number | null; totals: PipelineTotals | null }>('/api/deals', {
    refreshKey,
    map: (res) => ({ deals: res.deals ?? [], staleAfterDays: res.staleAfterDays ?? null, totals: res.totals ?? null }),
  });
  const deals = list.data?.deals ?? [];
  const staleAfterDays = list.data?.staleAfterDays ?? null;
  const totals = list.data?.totals ?? null;
  const setDeals = (fn: (ds: DealRow[]) => DealRow[]) =>
    list.setData((d) => ({ deals: fn(d?.deals ?? []), staleAfterDays: d?.staleAfterDays ?? null, totals: d?.totals ?? null }));

  // D-D3 / D-S3 — the stage fetch has its OWN state, and its failure lands in the PICKER, not the
  // list: a failed /api/stages must never render as an empty vocabulary ("— (none)" as the only
  // option teaches the operator they can unset a stage and not set one).
  const stagesFetch = useListFetch<ValueSheetOption[]>('/api/stages', {
    refreshKey,
    map: (rows) => (rows as any[]).map((st) => ({ value: st.id, label: st.name })),
  });
  const stages = stagesFetch.data ?? [];

  const applyStage = async (deal: DealRow, stageId: string | null) => {
    const prevStageId = deal.stageId;
    // B4: the picker list is LIVE stages only, so a deal sitting in a RETIRED stage cannot find
    // its own label there — the revert restores the row's previously-rendered name, never a
    // picker lookup. (Undoing INTO a retired stage is refused by the service and surfaces as the
    // failed-toast, visibly — nothing new enters a retired stage, undo included.)
    const prevStageNameRendered = deal.stageName;
    const stageName = stages.find((s) => s.value === stageId)?.label ?? null;
    setDeals((ds) => ds.map((d) => d.id === deal.id ? { ...d, stageId, stageName } : d)); // optimistic
    setEditing(null);
    try {
      const res = await fetch(`/api/deals/${deal.id}/stage`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stageId }) });
      if (!res.ok) throw new Error(String(res.status));
      setToast({ message: t('pipelineGrid.toast.updated'), variant: 'undo', onUndo: () => { setToast(null); void applyStage(deal, prevStageId); } });
    } catch {
      setDeals((ds) => ds.map((d) => d.id === deal.id ? { ...d, stageId: prevStageId, stageName: prevStageNameRendered } : d)); // revert
      setToast({ message: t('pipelineGrid.toast.failed'), variant: 'error' });
    }
  };

  const onEditStage = (deal: DealRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => setEditing({ deal, anchor, shape });

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="pipeline-grid" onRetry={list.retry}>
      {!isDesktop ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
          {deals.map((d) => <DealCardRow key={d.id} deal={d} staleAfterDays={staleAfterDays} selected={d.id === selectedId} onOpenDeal={onOpenDeal} onEditStage={onEditStage} />)}
          {deals.length === 0 && <div className="col-span-full text-center text-sm text-text-muted py-10">{t('pipelineGrid.empty')}</div>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-start border-collapse">
            <thead>
              <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                <th className="sticky z-10 bg-surface p-cell-y px-cell-x text-start [inset-inline-start:0]">{t('pipelineGrid.columns.deal')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('pipelineGrid.columns.stage')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('pipelineGrid.columns.lastStageChange')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('pipelineGrid.columns.value')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('pipelineGrid.columns.owner')}</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => <DealGridRow key={d.id} deal={d} staleAfterDays={staleAfterDays} selected={d.id === selectedId} onOpenDeal={onOpenDeal} onEditStage={onEditStage} />)}
            </tbody>
          </table>
          {deals.length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('pipelineGrid.empty')}</div>}
        </div>
      )}
      {/* B4/C2 — the honest totals (the X-4 site): the raw sum always renders when deals exist;
          the WEIGHTED figure exists only when the tenant declared probabilities, and it is
          labelled by its input coverage — never a bare number beside the raw one. */}
      {deals.length > 0 && totals && (
        <div className="flex items-center gap-4 flex-wrap px-3 py-2 border-t border-border-neutral text-sm" data-testid="pipeline-totals">
          <span data-testid="pipeline-total-unweighted" className="text-text-strong font-semibold" dir="ltr">
            {t('pipelineGrid.totals.unweighted', { value: totals.unweighted.toLocaleString(undefined, { maximumFractionDigits: 0 }) })}
          </span>
          {totals.weighted && (
            <span data-testid="pipeline-total-weighted" className="text-text-muted" dir="ltr">
              {t('pipelineGrid.totals.weighted', {
                value: totals.weighted.value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
                weighted: totals.weighted.weightedStages, total: totals.weighted.totalStages,
              })}
            </span>
          )}
        </div>
      )}
      </ListStateShell>

      {editing && stagesFetch.status !== 'error' && (
        <ValueSheet
          shape={editing.shape}
          title={`${t('pipelineGrid.columns.stage')} — ${editing.deal.name}`}
          options={stages}
          current={editing.deal.stageId}
          anchor={editing.anchor}
          onSelect={(v) => applyStage(editing.deal, v)}
          onClose={() => setEditing(null)}
        />
      )}
      {editing && stagesFetch.status === 'error' && (
        <SheetShell shape={editing.shape} anchor={editing.anchor} onClose={() => setEditing(null)} className={editing.shape === 'popover' ? 'w-64' : ''}>
          <div data-testid="stage-picker-error" className="p-4 flex flex-col items-center gap-3 text-center">
            <div className="text-sm text-text-muted">{t('listStates.error')}</div>
            <button type="button" data-testid="stage-picker-retry" onClick={stagesFetch.retry}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('listStates.retry')}
            </button>
          </div>
        </SheetShell>
      )}
      {toast && <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
    </div>
  );
};
